import { ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import {registerLocaleData} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MAT_DATE_LOCALE} from "@angular/material/core";
import {BrowserModule} from "@angular/platform-browser";
import {HttpClient, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import * as Sentry from "@sentry/angular";
import {Router} from "@angular/router";
import localeDE from "@angular/common/locales/de";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {StepService} from "../services/step.service";
import {TranslateLoader, TranslateNoOpLoader, provideTranslateService} from "@ngx-translate/core";
import {PortalService} from "../services/portal.service";
import {HeizcheckComponent} from "./heizcheck.component";
import {HeizcheckRoutingModule} from "./heizcheck-routing.module";
import {EinstiegComponent} from "./einstieg/einstieg.component";
import {EinstiegChapterComponent} from "./einstieg/einstieg.chapter.component";
import {VerbrauchsanalyseStepComponent} from "./verbrauchsanalyse/verbrauchsanalyse.step.component";
import {VerbrauchsanalyseHeizcheckStepVerbrauchsdatenComponent} from "./verbrauchsanalyse/verbrauchsanalyse-heizcheck.step.verbrauchsdaten.component";
import {ErgebnisComponent} from "./ergebnis/ergebnis.component";
import {HeizkostenabrechnungStartComponent} from "./heizkostenabrechnung/heizkostenabrechnung.start.component";
import {AdvisorVerbrauchsanalyseModule} from "../advisor.verbrauchsanalyse.module";
import {VerbrauchsanalyseHeizcheckAusstiegComponent} from "./verbrauchsanalyse/verbrauchsanalyse-heizcheck.ausstieg.component";
import {HeizkostenabrechnungStepComponent} from "./heizkostenabrechnung/heizkostenabrechnung.step.component";
import {AuswertenComponent} from "./auswerten/auswerten.component";
import {HeizkostenabrechnungAusstiegComponent} from "./heizkostenabrechnung/heizkostenabrechnung.ausstieg.component";
import {MatButtonModule} from "@angular/material/button";
import {CostRoundPipe} from "../pipe/CostRoundPipe";
import {AdvisorSharedModule} from "../advisor.shared.module";
import {SafeHtmlPipe} from "../pipe/SafeHtmlPipe";
import {HelptextComponent} from "../helptext/helptext.component";
import {TippsComponent} from "./tipps/tipps.component";
import {Co2InfoComponent} from "./co2info/co2info.component";
import {TippsOverlayComponent} from "./tipps/overlay/tipps-overlay.component";
import {HeizkostenabrechnungVerbrauchseingabeStepComponent} from "./heizkostenabrechnung/heizkostenabrechnung.verbrauchseingabe.step.component";
import {HeizkostenabrechnungKostenStepComponent} from "./heizkostenabrechnung/heizkostenabrechnung.kosten.step.component";
import {ErfassenComponent} from "./erfassen/erfassen.component";
import {MspComponent} from "./erfassen/msp.component";
import {TermsComponent} from "./erfassen/terms.component";
import {FileUploadComponent} from "../input/file-upload/file-upload.component";
import {UploadButtonComponent} from "../input/upload-button/upload-button.component";
import {FotoPreviewComponent} from "../dialog/foto-preview/foto-preview.component";
import {MatSnackBarModule} from "@angular/material/snack-bar";
import {InfoOverlayComponent} from "../dialog/noclosing/info-overlay.component";
import {FotoComponent} from "./foto/foto.component";
import {DocumentCameraComponent} from "../input/camera/document-camera.component";
import { ForecastChartComponent } from './forecast-chart/forecast-chart.component';
import {AdvisorEnergyMapModule} from '../advisor.energy.map.module';
import {HeizcheckEnergyMapComponent} from './energy-map/heizcheck.energy.map.component';
import {HeizcheckEnergyMapDonatedComponent} from './energy-map/heizcheck.energy.map.donated.component';

// global definition of language prefixe
declare let advisorTranslationPrefix;

// AoT requires an exported function for factories
export function HttpLoaderFactory() {
  if(document?.location?.host === 'localhost:4201') {
    // workaround for sowing only the textkeys in testing environment
    return new TranslateNoOpLoader();
  }
  const http = inject(HttpClient);
  return {
    getTranslation: (lang: string) => http.get<object>(
      (typeof advisorTranslationPrefix !== "undefined"? advisorTranslationPrefix : "translations/" + PortalService.defaultPortal + "/heizcheck/") + lang + ".json"
    )
  };
}

// load the locales dynamic, based on the users browser language with fallback
const browserLocale = getBrowsersLocale();
switch(browserLocale.substring(0,2)) {
  case 'de':
    registerLocaleData(localeDE, 'de');
    break;
}

/**
 * returns the users locale from the browser
 *
 * @returns {any}
 */
function getBrowsersLocale() {
  return 'de-DE';
}

@NgModule({
  declarations: [HeizcheckComponent, EinstiegComponent, EinstiegChapterComponent, VerbrauchsanalyseStepComponent, FotoPreviewComponent,
    VerbrauchsanalyseHeizcheckStepVerbrauchsdatenComponent, ErgebnisComponent, HeizkostenabrechnungStartComponent, VerbrauchsanalyseHeizcheckAusstiegComponent,
    HeizkostenabrechnungStepComponent, AuswertenComponent, HeizkostenabrechnungAusstiegComponent, CostRoundPipe, SafeHtmlPipe, ErfassenComponent, FileUploadComponent, UploadButtonComponent,
    HelptextComponent, TippsComponent, Co2InfoComponent, TippsOverlayComponent, HeizkostenabrechnungVerbrauchseingabeStepComponent, HeizkostenabrechnungKostenStepComponent, MspComponent,
    TermsComponent, InfoOverlayComponent, FotoComponent, DocumentCameraComponent, ForecastChartComponent, HeizcheckEnergyMapComponent, HeizcheckEnergyMapDonatedComponent
  ],
  exports: [
    SafeHtmlPipe,
  ],
  bootstrap: [
    HeizcheckComponent
  ],
  imports: [
    BrowserModule,
    HeizcheckRoutingModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    AdvisorVerbrauchsanalyseModule,
    AdvisorSharedModule,
    MatButtonModule,
    MatSnackBarModule,
    AdvisorEnergyMapModule
  ],
    providers: [
        {provide: MAT_DATE_LOCALE, useValue: 'de-DE'},
        {provide: LOCALE_ID, useValue: getBrowsersLocale()},
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: false,
                dialogOptions: {
                    user: {
                        name: 'dummy',
                        email: 'dummy@co2online.de'
                    }
                }
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        provideAppInitializer(() => {
          const initializerFn = () => {
            inject(Sentry.TraceService)
          };
        return initializerFn();
      }),
        StepService,
    provideTranslateService({
      loader: {provide: TranslateLoader, useFactory: HttpLoaderFactory}
    }),
        provideHttpClient(withInterceptorsFromDi())
  ]
})
export class HeizcheckModule {}
